/**
 * Spearman Thrust Effect - Spear thrust visual effects for melee attacks
 * Features:
 * - Thrust trail line following spear tip
 * - Impact flash and sparks on hit
 * - Ground dust effect
 * - Shockwave ripple for high damage attacks
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';

interface ThrustTrail {
  x: number;
  y: number;
  z: number;
  rotation: number;  // Direction the attacker is facing
  age: number;
  maxAge: number;
}

interface ImpactFlash {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  intensity: number;
}

interface SparkParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  maxAge: number;
  size: number;
}

interface DustRing {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  scale: number;
}

interface Shockwave {
  x: number;
  y: number;
  z: number;
  rotation: number;
  age: number;
  maxAge: number;
}

const MAX_THRUST_TRAILS = 100;
const MAX_IMPACT_FLASHES = 100;
const MAX_SPARKS = 400;
const MAX_DUST_RINGS = 50;
const MAX_SHOCKWAVES = 30;

// Shader for thrust trail (a forward-pointing line/streak)
const thrustTrailVertexShader = `
  attribute float alpha;
  varying float vAlpha;
  varying vec2 vUv;

  void main() {
    vAlpha = alpha;
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const thrustTrailFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  uniform float time;
  varying float vAlpha;
  varying vec2 vUv;

  void main() {
    // Trail gradient - brighter at the tip (front)
    float tipBrightness = pow(vUv.y, 0.3);

    // Fade based on progress
    float fade = 1.0 - progress;
    fade = pow(fade, 0.4);

    // Motion blur effect
    float blur = 1.0 - abs(vUv.x - 0.5) * 2.0;

    // Shimmer
    float shimmer = sin(vUv.y * 30.0 - time * 40.0) * 0.1 + 0.9;

    // Hot white core
    vec3 coreColor = mix(color, vec3(1.0, 1.0, 0.95), tipBrightness * 0.7);

    float alpha = tipBrightness * blur * fade * shimmer * vAlpha;

    gl_FragColor = vec4(coreColor, alpha);
  }
`;

// Shader for impact flash
const flashVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const flashFragmentShader = `
  uniform float progress;
  uniform float intensity;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - vec2(0.5);
    float dist = length(center) * 2.0;

    // Expanding ring with core
    float ring = 1.0 - smoothstep(0.0, 0.3, dist);
    float outerRing = 1.0 - smoothstep(progress * 0.5, progress, dist);
    outerRing *= smoothstep(0.0, progress * 0.3, dist);

    // Fast fade
    float fade = 1.0 - pow(progress, 0.5);

    float alpha = (ring * 0.8 + outerRing * 0.5) * fade * intensity;

    // White hot center
    vec3 finalColor = mix(color, vec3(1.0, 1.0, 0.9), ring);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Shader for sparks
const sparkVertexShader = `
  attribute float size;
  attribute float alpha;
  varying float vAlpha;

  void main() {
    vAlpha = alpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const sparkFragmentShader = `
  uniform vec3 color;
  varying float vAlpha;

  void main() {
    // Circular point with soft edges
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    if (dist > 0.5) discard;

    float alpha = (1.0 - dist * 2.0) * vAlpha;

    // Hot yellow-white core
    vec3 finalColor = mix(color, vec3(1.0, 1.0, 0.85), 1.0 - dist * 2.0);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Shader for dust ring
const dustVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const dustFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - vec2(0.5);
    float dist = length(center) * 2.0;

    // Expanding ring
    float ringSize = progress * 1.2;
    float ring = 1.0 - smoothstep(ringSize - 0.2, ringSize, dist);
    ring *= smoothstep(0.0, ringSize * 0.5, dist);

    // Fade out
    float fade = 1.0 - progress;

    float alpha = ring * fade * 0.35;

    gl_FragColor = vec4(color, alpha);
  }
`;

// Shader for shockwave
const shockwaveVertexShader = `
  varying vec2 vUv;
  varying float vHeight;

  void main() {
    vUv = uv;
    vHeight = position.y;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const shockwaveFragmentShader = `
  uniform float progress;
  uniform float time;
  uniform vec3 color;
  varying vec2 vUv;
  varying float vHeight;

  void main() {
    // Conical wave expanding forward
    float wavePos = progress * 1.5;
    float waveWidth = 0.12;

    // Distance from wave front
    float waveDist = abs(vUv.y - wavePos);
    float wave = 1.0 - smoothstep(0.0, waveWidth, waveDist);

    // Vertical fade
    float vertFade = 1.0 - abs(vHeight) * 2.5;
    vertFade = max(vertFade, 0.0);

    // Side fade (narrower at front)
    float sideFade = 1.0 - abs(vUv.x - 0.5) * 2.0;
    sideFade = pow(max(sideFade, 0.0), 0.5);

    // Overall fade
    float fade = 1.0 - progress;

    // Shimmer
    float shimmer = sin(vUv.y * 40.0 - time * 25.0) * 0.12 + 0.88;

    float alpha = wave * vertFade * sideFade * fade * shimmer * 0.65;

    // Steel blue core
    vec3 finalColor = mix(color, vec3(0.9, 0.95, 1.0), wave * 0.5);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

export class SpearmanThrustEffect {
  private scene: THREE.Scene;
  private time = 0;

  // Thrust trails
  private thrustTrails: ThrustTrail[] = [];
  private thrustGeometry: THREE.BufferGeometry;
  private thrustMaterials: THREE.ShaderMaterial[] = [];
  private thrustMeshes: THREE.Mesh[] = [];

  // Impact flashes
  private impactFlashes: ImpactFlash[] = [];
  private flashGeometry: THREE.PlaneGeometry;
  private flashMaterials: THREE.ShaderMaterial[] = [];
  private flashMeshes: THREE.Mesh[] = [];

  // Spark particles
  private sparks: SparkParticle[] = [];
  private sparkGeometry: THREE.BufferGeometry;
  private sparkMaterial: THREE.ShaderMaterial;
  private sparkPoints: THREE.Points;
  private sparkPositions: Float32Array;
  private sparkSizes: Float32Array;
  private sparkAlphas: Float32Array;

  // Dust rings
  private dustRings: DustRing[] = [];
  private dustGeometry: THREE.PlaneGeometry;
  private dustMaterials: THREE.ShaderMaterial[] = [];
  private dustMeshes: THREE.Mesh[] = [];

  // Shockwaves
  private shockwaves: Shockwave[] = [];
  private shockwaveGeometry: THREE.PlaneGeometry;
  private shockwaveMaterials: THREE.ShaderMaterial[] = [];
  private shockwaveMeshes: THREE.Mesh[] = [];

  // Colors
  private trailColor = new THREE.Color(0xaaddff);    // Light steel blue
  private flashColor = new THREE.Color(0xffffff);    // White flash
  private sparkColor = new THREE.Color(0xffcc55);    // Yellow-orange sparks
  private dustColor = new THREE.Color(0x8b7355);     // Brown dust
  private shockwaveColor = new THREE.Color(0x7799bb); // Steel blue

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Initialize thrust trail geometry
    this.thrustGeometry = this.createThrustGeometry();

    // Pre-create thrust trail meshes
    for (let i = 0; i < MAX_THRUST_TRAILS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: thrustTrailVertexShader,
        fragmentShader: thrustTrailFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.trailColor },
          time: { value: 0 },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.thrustMaterials.push(material);

      const mesh = new THREE.Mesh(this.thrustGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.thrustMeshes.push(mesh);
    }

    // Initialize impact flash geometry
    this.flashGeometry = new THREE.PlaneGeometry(1, 1);

    // Pre-create flash meshes
    for (let i = 0; i < MAX_IMPACT_FLASHES; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: flashVertexShader,
        fragmentShader: flashFragmentShader,
        uniforms: {
          progress: { value: 0 },
          intensity: { value: 1 },
          color: { value: this.flashColor },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.flashMaterials.push(material);

      const mesh = new THREE.Mesh(this.flashGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.flashMeshes.push(mesh);
    }

    // Initialize spark particles
    this.sparkPositions = new Float32Array(MAX_SPARKS * 3);
    this.sparkSizes = new Float32Array(MAX_SPARKS);
    this.sparkAlphas = new Float32Array(MAX_SPARKS);

    this.sparkGeometry = new THREE.BufferGeometry();
    this.sparkGeometry.setAttribute('position', new THREE.BufferAttribute(this.sparkPositions, 3));
    this.sparkGeometry.setAttribute('size', new THREE.BufferAttribute(this.sparkSizes, 1));
    this.sparkGeometry.setAttribute('alpha', new THREE.BufferAttribute(this.sparkAlphas, 1));

    this.sparkMaterial = new THREE.ShaderMaterial({
      vertexShader: sparkVertexShader,
      fragmentShader: sparkFragmentShader,
      uniforms: {
        color: { value: this.sparkColor },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.sparkPoints = new THREE.Points(this.sparkGeometry, this.sparkMaterial);
    this.sparkPoints.frustumCulled = false;
    this.scene.add(this.sparkPoints);

    // Initialize dust geometry
    this.dustGeometry = new THREE.PlaneGeometry(1, 1);

    // Pre-create dust meshes
    for (let i = 0; i < MAX_DUST_RINGS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: dustVertexShader,
        fragmentShader: dustFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.dustColor },
        },
        transparent: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.dustMaterials.push(material);

      const mesh = new THREE.Mesh(this.dustGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.dustMeshes.push(mesh);
    }

    // Initialize shockwave geometry
    this.shockwaveGeometry = new THREE.PlaneGeometry(1.5, 1.2, 12, 8);

    // Pre-create shockwave meshes
    for (let i = 0; i < MAX_SHOCKWAVES; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: shockwaveVertexShader,
        fragmentShader: shockwaveFragmentShader,
        uniforms: {
          progress: { value: 0 },
          time: { value: 0 },
          color: { value: this.shockwaveColor },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.shockwaveMaterials.push(material);

      const mesh = new THREE.Mesh(this.shockwaveGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.shockwaveMeshes.push(mesh);
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  private createThrustGeometry(): THREE.BufferGeometry {
    // Create a forward-pointing thrust trail (elongated triangle)
    const length = 0.8;  // Trail length
    const width = 0.15;  // Trail width

    const vertices: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const alphas: number[] = [];

    const segments = 12;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const y = t * length;

      // Width tapers toward tip
      const widthAtPoint = width * (1 - t * 0.8);

      // Left vertex
      vertices.push(-widthAtPoint / 2, 0, y);
      uvs.push(0, t);
      alphas.push(1 - t * 0.5);

      // Right vertex
      vertices.push(widthAtPoint / 2, 0, y);
      uvs.push(1, t);
      alphas.push(1 - t * 0.5);
    }

    // Create triangles
    for (let i = 0; i < segments; i++) {
      const base = i * 2;
      indices.push(base, base + 1, base + 2);
      indices.push(base + 1, base + 3, base + 2);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alphas, 1));
    geometry.setIndex(indices);

    return geometry;
  }

  private setupEventListeners(): void {
    // Listen for spear thrust event
    EventBus.on('spear:thrust', (data: {
      x: number;
      y: number;
      z: number;
      rotation: number;
      targetX: number;
      targetY: number;
      targetZ: number;
      damage: number;
    }) => {
      // Spawn thrust trail at attacker position
      this.spawnThrustTrail(data.x, data.y + 0.5, data.z, data.rotation);

      // Spawn impact flash at target
      const impactX = data.targetX;
      const impactY = data.targetY + 0.4;
      const impactZ = data.targetZ;
      this.spawnImpactFlash(impactX, impactY, impactZ, 0.7 + data.damage / 30);

      // Spawn sparks at impact
      this.spawnSparks(impactX, impactY, impactZ, 6 + Math.floor(data.damage / 6));

      // Spawn dust at ground level
      this.spawnDustRing(data.targetX, data.targetY + 0.05, data.targetZ);

      // Spawn shockwave for high damage attacks
      if (data.damage > 12) {
        this.spawnShockwave(data.x, data.y + 0.25, data.z, data.rotation);
      }
    });
  }

  private spawnThrustTrail(x: number, y: number, z: number, rotation: number): void {
    if (this.thrustTrails.length >= MAX_THRUST_TRAILS) {
      this.thrustTrails.shift();
    }

    this.thrustTrails.push({
      x,
      y,
      z,
      rotation,
      age: 0,
      maxAge: 0.2,
    });
  }

  private spawnImpactFlash(x: number, y: number, z: number, intensity: number): void {
    if (this.impactFlashes.length >= MAX_IMPACT_FLASHES) {
      this.impactFlashes.shift();
    }

    this.impactFlashes.push({
      x,
      y,
      z,
      age: 0,
      maxAge: 0.15,
      intensity: Math.min(intensity, 1.5),
    });
  }

  private spawnSparks(x: number, y: number, z: number, count: number): void {
    for (let i = 0; i < count && this.sparks.length < MAX_SPARKS; i++) {
      // Sparks fly outward in a cone
      const angle = Math.random() * Math.PI * 2;
      const upBias = 0.2 + Math.random() * 0.4;
      const speed = 1.5 + Math.random() * 2.5;

      this.sparks.push({
        x,
        y,
        z,
        vx: Math.cos(angle) * speed * (1 - upBias),
        vy: speed * upBias + Math.random() * 1.5,
        vz: Math.sin(angle) * speed * (1 - upBias),
        age: 0,
        maxAge: 0.25 + Math.random() * 0.35,
        size: 2.5 + Math.random() * 3.5,
      });
    }
  }

  private spawnDustRing(x: number, y: number, z: number): void {
    if (this.dustRings.length >= MAX_DUST_RINGS) {
      this.dustRings.shift();
    }

    this.dustRings.push({
      x,
      y,
      z,
      age: 0,
      maxAge: 0.4,
      scale: 0.6 + Math.random() * 0.3,
    });
  }

  private spawnShockwave(x: number, y: number, z: number, rotation: number): void {
    if (this.shockwaves.length >= MAX_SHOCKWAVES) {
      this.shockwaves.shift();
    }

    this.shockwaves.push({
      x,
      y,
      z,
      rotation,
      age: 0,
      maxAge: 0.35,
    });
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update thrust trails
    for (let i = this.thrustTrails.length - 1; i >= 0; i--) {
      const trail = this.thrustTrails[i];
      trail.age += deltaTime;

      if (trail.age >= trail.maxAge) {
        this.thrustTrails.splice(i, 1);
        continue;
      }

      const mesh = this.thrustMeshes[i];
      const material = this.thrustMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(trail.x, trail.y, trail.z);
        mesh.rotation.y = trail.rotation;

        // Animate the thrust
        const progress = trail.age / trail.maxAge;
        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;

        // Trail extends forward then fades
        const extension = Math.min(progress * 3, 1);
        mesh.scale.set(1, 1, extension);
      }
    }

    // Hide unused thrust meshes
    for (let i = this.thrustTrails.length; i < MAX_THRUST_TRAILS; i++) {
      this.thrustMeshes[i].visible = false;
    }

    // Update impact flashes
    for (let i = this.impactFlashes.length - 1; i >= 0; i--) {
      const flash = this.impactFlashes[i];
      flash.age += deltaTime;

      if (flash.age >= flash.maxAge) {
        this.impactFlashes.splice(i, 1);
        continue;
      }

      const mesh = this.flashMeshes[i];
      const material = this.flashMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(flash.x, flash.y, flash.z);

        // Billboard effect - face camera
        mesh.quaternion.copy(
          new THREE.Quaternion().setFromRotationMatrix(
            new THREE.Matrix4().lookAt(
              mesh.position,
              new THREE.Vector3(0, 0, 0),
              new THREE.Vector3(0, 1, 0)
            )
          )
        );

        const progress = flash.age / flash.maxAge;
        material.uniforms.progress.value = progress;
        material.uniforms.intensity.value = flash.intensity;

        // Expand quickly
        const scale = 0.3 + progress * 0.5;
        mesh.scale.setScalar(scale);
      }
    }

    // Hide unused flash meshes
    for (let i = this.impactFlashes.length; i < MAX_IMPACT_FLASHES; i++) {
      this.flashMeshes[i].visible = false;
    }

    // Update sparks
    const gravity = -12;
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      spark.age += deltaTime;

      if (spark.age >= spark.maxAge) {
        this.sparks.splice(i, 1);
        continue;
      }

      // Apply physics
      spark.vy += gravity * deltaTime;
      spark.x += spark.vx * deltaTime;
      spark.y += spark.vy * deltaTime;
      spark.z += spark.vz * deltaTime;

      // Bounce off ground
      if (spark.y < 0.05) {
        spark.y = 0.05;
        spark.vy = -spark.vy * 0.25;
        spark.vx *= 0.4;
        spark.vz *= 0.4;
      }
    }

    // Update spark geometry
    for (let i = 0; i < MAX_SPARKS; i++) {
      if (i < this.sparks.length) {
        const spark = this.sparks[i];
        const progress = spark.age / spark.maxAge;

        this.sparkPositions[i * 3] = spark.x;
        this.sparkPositions[i * 3 + 1] = spark.y;
        this.sparkPositions[i * 3 + 2] = spark.z;
        this.sparkSizes[i] = spark.size * (1 - progress * 0.6);
        this.sparkAlphas[i] = 1 - progress;
      } else {
        this.sparkAlphas[i] = 0;
      }
    }

    this.sparkGeometry.attributes.position.needsUpdate = true;
    this.sparkGeometry.attributes.size.needsUpdate = true;
    this.sparkGeometry.attributes.alpha.needsUpdate = true;

    // Update dust rings
    for (let i = this.dustRings.length - 1; i >= 0; i--) {
      const dust = this.dustRings[i];
      dust.age += deltaTime;

      if (dust.age >= dust.maxAge) {
        this.dustRings.splice(i, 1);
        continue;
      }

      const mesh = this.dustMeshes[i];
      const material = this.dustMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(dust.x, dust.y, dust.z);

        const progress = dust.age / dust.maxAge;
        const scale = dust.scale * (1 + progress * 1.5);
        mesh.scale.set(scale, scale, 1);

        material.uniforms.progress.value = progress;
      }
    }

    // Hide unused dust meshes
    for (let i = this.dustRings.length; i < MAX_DUST_RINGS; i++) {
      this.dustMeshes[i].visible = false;
    }

    // Update shockwaves
    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const wave = this.shockwaves[i];
      wave.age += deltaTime;

      if (wave.age >= wave.maxAge) {
        this.shockwaves.splice(i, 1);
        continue;
      }

      const mesh = this.shockwaveMeshes[i];
      const material = this.shockwaveMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(wave.x, wave.y, wave.z);
        mesh.rotation.y = wave.rotation;

        const progress = wave.age / wave.maxAge;
        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;

        // Wave expands forward
        const forwardOffset = progress * 1.5;
        mesh.position.x += Math.sin(wave.rotation) * forwardOffset;
        mesh.position.z += Math.cos(wave.rotation) * forwardOffset;

        // Scale up slightly
        mesh.scale.setScalar(1 + progress * 0.3);
      }
    }

    // Hide unused shockwave meshes
    for (let i = this.shockwaves.length; i < MAX_SHOCKWAVES; i++) {
      this.shockwaveMeshes[i].visible = false;
    }
  }

  dispose(): void {
    // Dispose thrust trails
    for (const mesh of this.thrustMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.thrustMaterials) {
      material.dispose();
    }
    this.thrustGeometry.dispose();
    this.thrustTrails = [];

    // Dispose impact flashes
    for (const mesh of this.flashMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.flashMaterials) {
      material.dispose();
    }
    this.flashGeometry.dispose();
    this.impactFlashes = [];

    // Dispose sparks
    this.scene.remove(this.sparkPoints);
    this.sparkGeometry.dispose();
    this.sparkMaterial.dispose();
    this.sparks = [];

    // Dispose dust rings
    for (const mesh of this.dustMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.dustMaterials) {
      material.dispose();
    }
    this.dustGeometry.dispose();
    this.dustRings = [];

    // Dispose shockwaves
    for (const mesh of this.shockwaveMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.shockwaveMaterials) {
      material.dispose();
    }
    this.shockwaveGeometry.dispose();
    this.shockwaves = [];
  }
}
